import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import type { KnowledgeLibraryResource } from "@/hooks/useKnowledgeLibrary";

interface VideoPlayerContextType {
  currentVideo: KnowledgeLibraryResource | null;
  isMinimized: boolean;
  playVideo: (resource: KnowledgeLibraryResource) => void;
  closeVideo: () => void;
  toggleMinimize: () => void;
}

const VideoPlayerContext = createContext<VideoPlayerContextType | null>(null);

export function VideoPlayerProvider({ children }: { children: ReactNode }) {
  const [currentVideo, setCurrentVideo] =
    useState<KnowledgeLibraryResource | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const playVideo = useCallback((resource: KnowledgeLibraryResource) => {
    setCurrentVideo(resource);
    setIsMinimized(false);
  }, []);

  const closeVideo = useCallback(() => {
    setCurrentVideo(null);
    setIsMinimized(false);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({
      currentVideo,
      isMinimized,
      playVideo,
      closeVideo,
      toggleMinimize,
    }),
    [currentVideo, isMinimized, playVideo, closeVideo, toggleMinimize],
  );

  return (
    <VideoPlayerContext.Provider value={contextValue}>
      {children}
    </VideoPlayerContext.Provider>
  );
}

export function useVideoPlayer() {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error("useVideoPlayer must be used within a VideoPlayerProvider");
  }
  return context;
}
